<script setup>

import {useProjects} from "@/store/projects";
import {computed, ref} from "vue";
import {t} from "@/controllerLK/GlobusLocalization";
import GlobusButtonPrimary from "@/components/common/GlobusInput/GlobusButtonPrimary.vue";
import BlockView from "@/components/common/BlockView.vue";
import {goLkPage, goMainPage} from "@/composables/ModuleCommunication";
import {useStore} from "@/store";
import {useRouter} from "vue-router";

const projectStore = useProjects();
const store = useStore();
const router = useRouter();

const isShowProjectDropdown = ref(false);
const isShowProjectTooltip = ref(false);
const titleTextRef = ref(null);

const isTextTruncated = (() => {
  if (!titleTextRef.value) return false;
  return titleTextRef.value.scrollWidth > titleTextRef.value.clientWidth;
});

const projectList = computed(() => {
  return projectStore.projects.filter((project) => {
    return project.id !== projectStore.activeProject.id && project.modules.includes(store.activeModule.dataName);
  });
});

const setActiveProject = async (project) => {
  store.loader = true;
  isShowProjectDropdown.value = false;
  await projectStore.setActiveProject(project.id);
  store.loader = false;
};

const moveToProjects = () => {
  localStorage.setItem("lk_tab", "projects");
  isShowProjectDropdown.value = false;
  goLkPage();
};

const handleLogoClick = () => {
  if (projectStore.projects.length === 0) {
    store.createNotify(t("toast.error.no_project"), "error");
    return false;
  } else {
    goMainPage();
  }
};

</script>

<template>
  <div
    v-if="router.currentRoute.value.fullPath.includes('/lk/')"
    class="project"
  >
    <div
      class="project__title"
      @click="handleLogoClick"
    >
      <span class="project__title-text">
        {{ t('header_project_menu.button.back') }}
      </span>
    </div>
  </div>
  <div
    v-else
    class="project"
  >
    <div
      class="project__title"
      :class="{
        'project__title_active': isShowProjectDropdown,
        'project__title_map-page': router.currentRoute.value.fullPath === '/map'
      }"
      @mouseenter="isShowProjectTooltip = true"
      @mouseleave="isShowProjectTooltip = false"
      @click="isShowProjectDropdown = !isShowProjectDropdown"
    >
      <span
        ref="titleTextRef"
        class="project__title-text"
      >
        <template v-if="projectStore.activeProject.name">
          {{ t('header_project_menu.label.project') }}:
          {{ projectStore.activeProject.name }}
        </template>
        <template v-else>
          {{ t('header_project_menu.label.no_project') }}
        </template>
      </span>
    </div>
    <div
      v-if="isShowProjectTooltip && projectStore.activeProject.name && isTextTruncated()"
      class="project__tooltip"
    >
      <span class="title">
        {{ t('header_project_menu.label.project') }}:
      </span> 
      {{ projectStore.activeProject.name }}
    </div>
    <div
      v-if="isShowProjectDropdown"
      class="project-dropdown"
    >
      <div class="project-dropdown__list scroll">
        <div
          v-if="projectStore.activeProject.name"
          class="project-dropdown__item_active"
        >
          <!--<HeaderServices v-if="useHeaderMenu && !store.isProd" /> (Задача 2248) -->
          <template v-if="projectStore.activeProject.name">
            <div class="item__title">
              {{ projectStore?.activeProject?.name }}
            </div>
            <div class="item__subtitle">
              {{ t('header_project_menu.label.current_project') }}
            </div>
          </template>
        </div>
        <div
          v-for="project in projectList"
          :key="project.id"
          class="project-dropdown__item"
          @click="setActiveProject(project)"
        >
          <div class="project-dropdown__item_text">
            {{ project.name }}
          </div>
        </div>
      </div>
      <GlobusButtonPrimary @click="moveToProjects">
        {{ t('header_project_menu.button.open') }}
      </GlobusButtonPrimary>
    </div>
  </div>
  <BlockView
    v-if="isShowProjectDropdown"
    @click="isShowProjectDropdown = false"
  />
</template>

<style scoped lang="scss">
.project {
  display: flex;
  justify-content: flex-end;
  max-width: 270px;
  position: relative;

  &__tooltip {

    background: #FFFFFF;
    padding: 10px 14px;
    border-radius: 8px;
    position: absolute;
    right: 0;
    top: 43px;
    box-shadow: 1px 2px 10px 0 #131E2726;
    line-height: 19.6px;
    font-size: 14px;
    font-weight: 400;

    .title {
      font-weight: 500;
      font-size: 14px;
    }


  }


  &__icon {
    width: 16px;
    height: 16px;
    color: #373D3F;
  }

  &-dropdown {
    display: flex;
    flex-direction: column;
    gap: 4px;
    position: absolute;
    top: 43px;
    right: 0;
    background: #ffffff;
    padding: 14px;
    box-shadow: 1px 2px 10px 0 #131E2726;
    z-index: 2000;
    border-radius: 8px;
    width: 260px;

    &__list {
      max-height: 80vh;
    }

    &__item {
      display: flex;
      align-items: center;
      width: 100%;
      cursor: pointer;
      padding: 6px 14px;
      height: 30px;
      transition: all .2s ease-in-out;

      &_text {
        display: inline;
        white-space: nowrap;
        word-wrap: break-word;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      &_active {
        width: 100%;
        display: grid;
        grid-template-columns: 3fr 2fr;
        padding: 6px 14px;

        .item {
          display: flex;
          justify-content: space-between;

          &__title {
            white-space: nowrap;
            word-wrap: break-word;
            overflow: hidden;
            text-overflow: ellipsis;
            color: #1F9AFF;
          }

          &__subtitle {
            color: #A7A7A7;
            text-align: end;
          }
        }
      }

      &:hover {
        border-radius: 8px;
        background: #E0F0FF;
      }
    }
  }

  &__title {
    display: flex;
    justify-content: center;
    align-items: center;
    color: #373D3F;

    align-content: center;
    cursor: pointer;
    user-select: none;
    border-radius: 22px;
    background: #FFF;
    padding: 6px 14px;
    gap: 5px;
    height: 36px;
    min-width: 0;
    max-width: 100%;

    &-text {
      display: block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }

    &:hover {
      color: #1F9AFF;
    }

    &_active {
      background: rgba(123, 135, 165, 0.1);
      color: #1F9AFF;
    }

    &_map-page {
      background: #F7F8FD;
    }
  }
}
</style>
